// Per-seat and per-category seat materials (CIN-15126): which color, texture and accent a seat part is drawn with,
// how the parts are grouped into instanced meshes (one per texture), and how a textured material is tinted to a
// color. One rule for the studio and the guest map. Pure; relative imports only.
import type { HexColor, SeatMaterials, SeatPartMaterial, SurfaceMaterial, TextureId } from './room-design.ts';
import { TEXTURE_CATALOG } from './texture-catalog.ts';

/** What the rule needs of a seat: its server id (null while unsaved), its category id and, for an unsaved one, its key. */
export type MaterialSeat = {
    id: number | string | null;
    categoryId: number | string | null | undefined;
    key?: string;
};

/** The entry key of a seat: its server id, or `k:<client key>` until the create resolved (the studio re-keys then). */
export const PENDING_SEAT_PREFIX = 'k:';
export function seatMaterialKey(seat: Pick<MaterialSeat, 'id' | 'key'>): string | null {
    if (seat.id !== null) return String(seat.id);
    return seat.key === undefined ? null : `${PENDING_SEAT_PREFIX}${seat.key}`;
}

/** One part after the precedence seat > category > (nothing: the design surface), field by field. */
export type ResolvedPart = { color: HexColor | null; texture: TextureId | null; accent: HexColor | null };
export type ResolvedSeatMaterial = { cushion: ResolvedPart; shell: ResolvedPart };

export const hasSeatMaterials = (materials: SeatMaterials): boolean =>
    Object.keys(materials.bySeat).length > 0 || Object.keys(materials.byCategory).length > 0;

const EMPTY_PART: ResolvedPart = { color: null, texture: null, accent: null };

function resolvePart(own: SeatPartMaterial | undefined, category: SeatPartMaterial | undefined): ResolvedPart {
    if (!own && !category) return EMPTY_PART;
    return {
        color: own?.color ?? category?.color ?? null,
        texture: own?.texture ?? category?.texture ?? null,
        accent: own?.accent ?? category?.accent ?? null
    };
}

/** Seat entry first, then the category's, per part and per field (a seat that sets only a texture keeps the category color). */
export function resolveSeatMaterial(materials: SeatMaterials, seat: MaterialSeat): ResolvedSeatMaterial {
    const ownKey = seatMaterialKey(seat);
    const own = ownKey === null ? undefined : materials.bySeat[ownKey];
    const category =
        seat.categoryId === null || seat.categoryId === undefined
            ? undefined
            : materials.byCategory[String(seat.categoryId)];
    return { cushion: resolvePart(own?.cushion, category?.cushion), shell: resolvePart(own?.shell, category?.shell) };
}

const srgbToLinear = (byte: number): number => {
    const c = byte / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

/** A #rrggbb color as linear rgb (the space three.js multiplies instance colors in). */
export function linearOf(hex: string): [number, number, number] {
    const value = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : '#808080';
    return [1, 3, 5].map((i) => srgbToLinear(parseInt(value.slice(i, i + 2), 16))) as [number, number, number];
}

/** The instance color is at most this many times the material's own (a very dark surface tinted very bright). */
export const MAX_TINT = 8;
const MIN_BASE = 0.004;

/**
 * The linear factor that turns a material painted in `base` into `target` when multiplied in: a per-instance color
 * over a shared textured material. The texture keeps its pattern and light/dark variation, its average becomes the
 * target. A target darker or brighter than the material by more than `MAX_TINT` is clipped.
 */
export function tintRatio(target: string, base: string): [number, number, number] {
    const t = linearOf(target);
    const b = linearOf(base);
    return [0, 1, 2].map((i) => Math.min(MAX_TINT, t[i] / Math.max(b[i], MIN_BASE))) as [number, number, number];
}

/** The color a group of another texture is painted in: light and neutral, so any target color is a modest tint. */
export const NEUTRAL_BASE: HexColor = '#b4b4b4';

/** The color a surface actually shows: an uploaded image is not painted in `color`. */
export const surfaceBase = (surface: SurfaceMaterial): string =>
    surface.texture === 'image' ? '#ffffff' : surface.color;

/** Textures without a second color ignore the accent, so two seats differing only in it share a group. */
const accentOf = (texture: TextureId, accent: HexColor | null, room: SurfaceMaterial): HexColor =>
    TEXTURE_CATALOG[texture].hasAccent ? (accent ?? room.accent) : room.accent;

/** How one seat part is drawn: the group (= mesh and material) it belongs to and the tint of its instance. */
export type PartLook = {
    /** `<texture>|<accent>`; seats with the same key share one instanced mesh. */
    groupKey: string;
    /** The material the group is painted in: the room's own surface when the key is the room's, else a derived one. */
    surface: SurfaceMaterial;
    /** The color the part shows once tinted. */
    color: HexColor;
    /** The instance color that turns the group's material into `color`; null (white) when it already is. */
    tint: [number, number, number] | null;
    /** True when the part has a texture or accent of its own, so it is not in the room's group. */
    textured: boolean;
    /** True when a seat or category set the color (else `color` is the room's). */
    hasColor: boolean;
};

const groupKeyOf = (surface: Pick<SurfaceMaterial, 'texture' | 'accent'>): string =>
    `${surface.texture}|${surface.accent.toLowerCase()}`;

/** The look of a part with nothing of its own: the room's surface as painted. */
export function roomLook(room: SurfaceMaterial): PartLook {
    return {
        groupKey: groupKeyOf(room),
        surface: room,
        color: room.color,
        tint: null,
        textured: false,
        hasColor: false
    };
}

/** The look of one resolved part over the room's surface for that part. */
export function partLook(resolved: ResolvedPart, room: SurfaceMaterial): PartLook {
    const roomKey = groupKeyOf(room);
    const texture = resolved.texture ?? room.texture;
    const accent = accentOf(texture, resolved.accent, room);
    const groupKey = groupKeyOf({ texture, accent });
    const inRoomGroup = groupKey === roomKey;
    const surface: SurfaceMaterial = inRoomGroup
        ? room
        : { ...room, texture, accent, color: NEUTRAL_BASE, imageUrl: texture === 'image' ? room.imageUrl : null };
    const color = resolved.color ?? room.color;
    const base = surfaceBase(surface);
    return {
        groupKey,
        surface,
        color,
        tint: color.toLowerCase() === base.toLowerCase() ? null : tintRatio(color, base),
        textured: !inRoomGroup,
        hasColor: resolved.color !== null
    };
}

export type SeatLooks = { cushion: PartLook; shell: PartLook };

/** More textures than this on one screen and the least used ones fall back to the room's texture (their color stays). */
export const MAX_LOOK_GROUPS = 12;

export type LookOptions = {
    /** False draws colors only (the low-quality path): every texture and accent is the room's. */
    textures?: boolean;
    maxGroups?: number;
};

/**
 * The look of every seat that has an entry (its own or its category's), keyed by `keyOf(seat)`. A seat without
 * one is not in the map: callers draw it with `roomLook`. Groups beyond `maxGroups` (the least used first) keep
 * their color but lose the texture, so a room where every seat is different still renders as a bounded set of meshes.
 */
export function resolveSeatLooks<S extends MaterialSeat, K>(
    materials: SeatMaterials,
    seats: readonly S[],
    keyOf: (seat: S) => K,
    room: { seats: SurfaceMaterial; seatShell: SurfaceMaterial },
    options: LookOptions = {}
): Map<K, SeatLooks> {
    const { textures = true, maxGroups = MAX_LOOK_GROUPS } = options;
    const out = new Map<K, SeatLooks>();
    if (!hasSeatMaterials(materials)) return out;
    const strip = (part: ResolvedPart): ResolvedPart =>
        textures ? part : { color: part.color, texture: null, accent: null };
    const resolved = new Map<K, ResolvedSeatMaterial>();
    const uses = new Map<string, number>();
    for (const seat of seats) {
        const material = resolveSeatMaterial(materials, seat);
        if (material.cushion === EMPTY_PART && material.shell === EMPTY_PART) continue;
        const cushion = strip(material.cushion);
        const shell = strip(material.shell);
        resolved.set(keyOf(seat), { cushion, shell });
        for (const [part, surface] of [
            [cushion, room.seats],
            [shell, room.seatShell]
        ] as const) {
            const key = `${surface === room.seats ? 'c' : 's'}:${partLook(part, surface).groupKey}`;
            uses.set(key, (uses.get(key) ?? 0) + 1);
        }
    }
    const roomKeys = new Set([`c:${groupKeyOf(room.seats)}`, `s:${groupKeyOf(room.seatShell)}`]);
    const kept = new Set(
        [...uses]
            .filter(([key]) => !roomKeys.has(key))
            .sort((a, b) => b[1] - a[1])
            .slice(0, maxGroups)
            .map(([key]) => key)
    );
    const admit = (part: ResolvedPart, surface: SurfaceMaterial, tag: 'c' | 's'): PartLook => {
        const look = partLook(part, surface);
        if (!look.textured || kept.has(`${tag}:${look.groupKey}`)) return look;
        return partLook({ ...part, texture: null, accent: null }, surface);
    };
    for (const [key, material] of resolved) {
        out.set(key, {
            cushion: admit(material.cushion, room.seats, 'c'),
            shell: admit(material.shell, room.seatShell, 's')
        });
    }
    return out;
}

export type LookGroup<T> = { key: string; surface: SurfaceMaterial; items: T[] };

/** Splits instances into the groups (one mesh each) of their look, in first-seen order; empty groups do not exist. */
export function groupByLook<T>(items: readonly T[], lookOf: (item: T) => PartLook): LookGroup<T>[] {
    const groups = new Map<string, LookGroup<T>>();
    for (const item of items) {
        const look = lookOf(item);
        const group = groups.get(look.groupKey);
        if (group) group.items.push(item);
        else groups.set(look.groupKey, { key: look.groupKey, surface: look.surface, items: [item] });
    }
    return [...groups.values()];
}
